import { Input } from 'antd'
import { InputProps } from 'antd/lib/input'
import React, { forwardRef, ReactNode, useState } from 'react'
import NumberFormat, { NumberFormatProps } from 'react-number-format'

/** 自定义输入框，本来用Input即可，但prefix和suffix这两个属性与NumberFormat相冲突 */
interface AntInputNodeProps extends InputProps {
  antPrefix: ReactNode;
  antSuffix: ReactNode;
}

class AntInput extends React.Component<AntInputNodeProps> {
  render (): JSX.Element {
    const { antPrefix: prefix, antSuffix: suffix, ...props } = this.props
    return (
      <Input prefix={prefix} suffix={suffix} {...props} />
    )
  }
}

/** 数字输入框 */
export interface InputNumberProps extends Omit<AntInputNodeProps, 'value' | 'onChange' | 'onBlur' | 'prefix' | 'suffix' | 'size' | 'type'> {
  value?: number | string; /** 受控属性 */
  defaultValue?: number | string; /** 默认value */
  max?: number; /** 限制最大范围 */
  min?: number; /** 最小范围 */
  precision?: number; /** 小数位数 */
  rangBorder?: 0 | 1; /** 当输入均不在最大及最小范围内时，默认取值偏向哪边，0 -> min, 1 -> max */
  thousandSeparator?: boolean; /** 是否千分符分割 */
  format?: NumberFormatProps['format']; /** 格式化 */
  onChange?: (value: string) => void; /** value改变时触发 */
  onBlur?: (value: string) => void; /** input失去焦点时触发 */
  [key: string]: any;
}

// eslint-disable-next-line react/display-name
export const InputNumber = forwardRef<NumberFormatProps['getInputRef'], InputNumberProps>((props: InputNumberProps, ref): JSX.Element => {
  const {
    value: controlValue,
    defaultValue = '',
    max = Number.MAX_SAFE_INTEGER,
    min = Number.MIN_SAFE_INTEGER,
    precision = 8,
    thousandSeparator = true,
    format,
    onChange,
    onBlur,
    ...parentProps
  } = props

  const [value, setValue] = useState<number | string>(defaultValue)

  /**
   * 输入框值改变时处理函数
   * @param value 输入框值
   */
  const handleOnChange = (value = ''): void => {
    const updateValue = value === '' || format !== undefined || +value <= max ? value : max.toString()
    setValue(updateValue)
    onChange && onChange(updateValue)
  }

  /**
   * 输入框失去焦点时处理函数
   */
  const handleOnBlur: () => void = () => {
    const tempValue = value.toString()

    value !== tempValue && handleOnChange(tempValue)
    onBlur && onBlur(tempValue)
  }

  const numberFormatProps: NumberFormatProps = {
    value: controlValue === undefined ? value : controlValue,
    isNumericString: true,
    thousandSeparator,
    decimalScale: precision,
    allowNegative: min < 0,
    customInput: AntInput,
    format,
    onBlur: handleOnBlur,
    onValueChange: values => handleOnChange(values.value),
    isAllowed: ({ floatValue }) => floatValue === undefined || format !== undefined || floatValue <= max
  }

  return (
    <NumberFormat getInputRef={ref} {...numberFormatProps} {...parentProps} />
  )
})
